import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  listDispositionRulesV2, upsertDispositionRuleV2, evaluateDisposition,
} from "@/lib/returns-disposition.functions";
import {
  recommendRecoveryTarget, executeRecovery, OVERRIDE_REASONS,
} from "@/lib/returns-recovery.functions";
import { computeReturnProcessingSla } from "@/lib/returns-processing-sla.functions";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

export const Route = createFileRoute("/_authenticated/app/returns-disposition")({
  component: DispositionRulesPage,
});

const SOURCES = ["any","customer_return","return_to_sender","manual_unknown"] as const;
const CONDITIONS = ["any","sellable","opened","damaged","unsalvageable"] as const;
const DISPOSITIONS = ["restock","quarantine","destroy","return_to_vendor_or_liquidator","refurbish"] as const;
const PUTBACK = ["any","pickface","reserve","staging"] as const;

function DispositionRulesPage() {
  const qc = useQueryClient();
  const { tenantId } = useScope();
  const listFn = useServerFn(listDispositionRulesV2);
  const upsertFn = useServerFn(upsertDispositionRuleV2);

  const { data } = useQuery({
    queryKey: ["disposition-rules-v2", tenantId],
    enabled: !!tenantId,
    queryFn: () => listFn({ data: { tenantId: tenantId! } }),
  });

  const [source, setSource] = useState<typeof SOURCES[number]>("any");
  const [cond, setCond] = useState<typeof CONDITIONS[number]>("any");
  const [disp, setDisp] = useState<typeof DISPOSITIONS[number]>("restock");
  const [putback, setPutback] = useState<typeof PUTBACK[number]>("pickface");
  const [reviewer, setReviewer] = useState(false);
  const [priority, setPriority] = useState(100);
  const [notes, setNotes] = useState("");
  const [reasonsAny, setReasonsAny] = useState("");
  const [reasonsNone, setReasonsNone] = useState("");

  const add = useMutation({
    mutationFn: () => upsertFn({ data: {
      tenantId: tenantId!,
      returnSource: source === "any" ? null : source,
      inspectionCondition: cond === "any" ? null : cond,
      reasonCodesAny: reasonsAny.trim() ? reasonsAny.split(",").map(s => s.trim()).filter(Boolean) : null,
      reasonCodesNone: reasonsNone.trim() ? reasonsNone.split(",").map(s => s.trim()).filter(Boolean) : null,
      disposition: disp,
      putbackTarget: putback === "any" ? null : putback,
      reviewerRequired: reviewer,
      priority,
      active: true,
      notes: notes.trim() || null,
    } }),
    onSuccess: () => {
      toast.success("Rule added");
      qc.invalidateQueries({ queryKey: ["disposition-rules-v2"] });
      setNotes(""); setReasonsAny(""); setReasonsNone("");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <>
      <PageHeader
        title="Disposition Policy"
        description="Sprint 20 Inventory Recovery. Rules map (source + condition + reason codes) to a standardized disposition. Lower priority wins. Decisions are recorded with a PolicyExplanation; physical motion still flows through the ledger-authoritative engine."
      />
      <div className="px-4 sm:px-6 py-4 space-y-4">
        <div>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/app/returns"><ArrowLeft className="h-4 w-4 mr-1" />Back to returns</Link>
          </Button>
        </div>

        <Card>
          <CardHeader><CardTitle className="text-sm">Add Rule (v2)</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <Field label="Return source">
              <Select value={source} onValueChange={(v) => setSource(v as any)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Inspection condition">
              <Select value={cond} onValueChange={(v) => setCond(v as any)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CONDITIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Disposition">
              <Select value={disp} onValueChange={(v) => setDisp(v as any)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{DISPOSITIONS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Putback target">
              <Select value={putback} onValueChange={(v) => setPutback(v as any)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PUTBACK.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Reason codes ANY (comma-sep)">
              <Input value={reasonsAny} onChange={(e) => setReasonsAny(e.target.value)} placeholder="e.g. DEFECTIVE,NEEDS_QA" />
            </Field>
            <Field label="Reason codes NONE (block)">
              <Input value={reasonsNone} onChange={(e) => setReasonsNone(e.target.value)} placeholder="e.g. CARRIER_DAMAGE" />
            </Field>
            <Field label="Priority">
              <Input type="number" value={priority} onChange={(e) => setPriority(Math.max(1, Number(e.target.value) || 100))} />
            </Field>
            <Field label="Reviewer required">
              <Select value={reviewer ? "yes" : "no"} onValueChange={(v) => setReviewer(v === "yes")}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="no">No</SelectItem>
                  <SelectItem value="yes">Yes</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Notes">
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
            </Field>
            <div className="flex items-end">
              <Button onClick={() => add.mutate()} disabled={add.isPending || !tenantId}>Add rule</Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-sm">Active Rules</CardTitle></CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Pri</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Condition</TableHead>
                  <TableHead>Reasons ANY</TableHead>
                  <TableHead>Reasons NONE</TableHead>
                  <TableHead>Disposition</TableHead>
                  <TableHead>Putback</TableHead>
                  <TableHead>Reviewer</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.rules ?? []).length === 0 && (
                  <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">No rules.</TableCell></TableRow>
                )}
                {(data?.rules ?? []).map((r: any) => (
                  <TableRow key={r.id}>
                    <TableCell className="tabular-nums">{r.priority}</TableCell>
                    <TableCell className="text-xs">{r.return_source ?? <Muted>any</Muted>}</TableCell>
                    <TableCell className="text-xs">{r.inspection_condition ?? <Muted>any</Muted>}</TableCell>
                    <TableCell className="text-xs">{(r.reason_codes_any ?? []).join(", ") || <Muted>—</Muted>}</TableCell>
                    <TableCell className="text-xs">{(r.reason_codes_none ?? []).join(", ") || <Muted>—</Muted>}</TableCell>
                    <TableCell><Badge>{r.disposition}</Badge></TableCell>
                    <TableCell className="text-xs">{r.putback_target ?? <Muted>—</Muted>}</TableCell>
                    <TableCell><Badge variant={r.reviewer_required ? "destructive" : "secondary"}>{r.reviewer_required ? "required" : "no"}</Badge></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{r.notes}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <DecisionPreview />
        <RecoveryPreview />
        <ProcessingSlaPreview />
      </div>
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return <span className="text-muted-foreground">{children}</span>;
}

function DecisionPreview() {
  const evalFn = useServerFn(evaluateDisposition);
  const [rmaLineId, setRmaLineId] = useState("");
  const [src, setSrc] = useState<typeof SOURCES[number]>("customer_return");
  const [cond, setCond] = useState<typeof CONDITIONS[number]>("sellable");
  const [reasons, setReasons] = useState("");
  const [result, setResult] = useState<any | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!rmaLineId.trim()) { toast.error("Paste an RMA line UUID"); return; }
    setBusy(true);
    try {
      const r = await evalFn({ data: {
        rmaLineId: rmaLineId.trim(),
        overrideInputs: {
          returnSource: src === "any" ? null : src as any,
          inspectionCondition: cond === "any" ? null : cond as any,
          reasonCodes: reasons.trim() ? reasons.split(",").map(s => s.trim()).filter(Boolean) : [],
        },
      } });
      setResult(r);
    } catch (e: any) {
      toast.error(e?.message ?? "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Card>
      <CardHeader><CardTitle className="text-sm">Decision Preview</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <Field label="RMA line ID">
            <Input value={rmaLineId} onChange={(e) => setRmaLineId(e.target.value)} placeholder="uuid" />
          </Field>
          <Field label="Source (override)">
            <Select value={src} onValueChange={(v) => setSrc(v as any)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Condition (override)">
            <Select value={cond} onValueChange={(v) => setCond(v as any)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{CONDITIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Reason codes (comma)">
            <Input value={reasons} onChange={(e) => setReasons(e.target.value)} placeholder="DEFECTIVE,CARRIER_DAMAGE" />
          </Field>
        </div>
        <Button onClick={run} disabled={busy}>Evaluate</Button>
        {result && (
          <div className="rounded border border-border bg-muted/30 p-3 text-sm space-y-2">
            <div className="font-medium">{result.explanation.headline}</div>
            <ul className="list-disc list-inside text-xs text-muted-foreground space-y-1">
              {(result.explanation.detail as string[]).map((d, i) => <li key={i}>{d}</li>)}
            </ul>
            <div className="text-xs"><span className="text-muted-foreground">Source:</span> {result.explanation.source}</div>
            <details className="text-xs">
              <summary className="cursor-pointer text-muted-foreground">Provenance JSON</summary>
              <pre className="mt-2 overflow-auto">{JSON.stringify(result.decision, null, 2)}</pre>
            </details>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RecoveryPreview() {
  const recFn = useServerFn(recommendRecoveryTarget);
  const execFn = useServerFn(executeRecovery);
  const [dispId, setDispId] = useState("");
  const [rec, setRec] = useState<any | null>(null);
  const [actual, setActual] = useState("");
  const [reason, setReason] = useState<typeof OVERRIDE_REASONS[number]>("operator_judgment");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!dispId.trim()) { toast.error("Paste an rma_disposition UUID"); return; }
    setBusy(true);
    try {
      const r = await recFn({ data: { rmaDispositionId: dispId.trim() } });
      setRec(r);
      setActual(r.recommendedLocationId ?? "");
    } catch (e: any) { toast.error(e?.message ?? "Failed"); }
    finally { setBusy(false); }
  };

  const isOverride = rec?.recommendedLocationId && actual && actual !== rec.recommendedLocationId;

  const exec = async () => {
    if (!actual.trim()) { toast.error("Pick a destination"); return; }
    setBusy(true);
    try {
      const r = await execFn({ data: {
        rmaDispositionId: dispId.trim(),
        actualLocationId: actual.trim(),
        overrideReasonCode: isOverride ? reason : undefined,
        overrideNote: isOverride ? (note.trim() || undefined) : undefined,
      } });
      toast.success(r.override ? "Executed with override" : "Executed via recommendation");
    } catch (e: any) { toast.error(e?.message ?? "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Card>
      <CardHeader><CardTitle className="text-sm">Recovery Preview</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="rma_disposition ID">
            <Input value={dispId} onChange={(e) => setDispId(e.target.value)} placeholder="uuid" />
          </Field>
          <div className="flex items-end">
            <Button onClick={run} disabled={busy} variant="secondary">Recommend</Button>
          </div>
        </div>
        {rec && (
          <div className="rounded border border-border bg-muted/30 p-3 text-sm space-y-2">
            <div className="font-medium">{rec.explanation.headline}</div>
            <ul className="list-disc list-inside text-xs text-muted-foreground space-y-1">
              {(rec.explanation.detail as string[]).map((d: string, i: number) => <li key={i}>{d}</li>)}
            </ul>
            {(rec.candidates ?? []).length > 0 && (
              <div className="text-xs">
                <div className="text-muted-foreground mb-1">Candidates:</div>
                <ul className="space-y-1">
                  {(rec.candidates as any[]).map((c) => (
                    <li key={c.locationId || c.locationCode} className="flex items-center gap-2">
                      <Badge variant="secondary">{c.bucket}</Badge>
                      <span className="font-mono">{c.locationCode}</span>
                      <span className="text-muted-foreground">— {c.reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
              <Field label="Actual destination location ID">
                <Input value={actual} onChange={(e) => setActual(e.target.value)} placeholder="uuid" />
              </Field>
              {isOverride && (
                <>
                  <Field label="Override reason">
                    <Select value={reason} onValueChange={(v) => setReason(v as any)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {OVERRIDE_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Override note (optional)">
                    <Input value={note} onChange={(e) => setNote(e.target.value)} />
                  </Field>
                </>
              )}
            </div>
            <Button onClick={exec} disabled={busy}>{isOverride ? "Execute with override" : "Execute"}</Button>
            <div className="text-xs text-muted-foreground">
              Movement runs through the existing inv_return engine. Allocation state is not modified by this recommendation.
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ProcessingSlaPreview() {
  const fn = useServerFn(computeReturnProcessingSla);
  const [rmaId, setRmaId] = useState("");
  const [result, setResult] = useState<any | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!rmaId.trim()) { toast.error("Paste an RMA UUID"); return; }
    setBusy(true);
    try {
      const r = await fn({ data: { rmaId: rmaId.trim() } });
      setResult(r);
    } catch (e: any) { toast.error(e?.message ?? "Failed"); }
    finally { setBusy(false); }
  };

  const fmt = (iso: string | null) => iso ? new Date(iso).toLocaleString() : "—";
  const fmtRem = (m: number | null) => {
    if (m === null) return "—";
    if (m < 0) return `${Math.abs(m)}m late`;
    const h = Math.floor(m / 60); const mm = m % 60;
    return h ? `${h}h ${mm}m left` : `${mm}m left`;
  };
  const statusVariant = (s: string): "default" | "secondary" | "destructive" | "outline" => {
    switch (s) {
      case "met": return "secondary";
      case "pending": return "outline";
      case "at_risk": return "default";
      case "breached": return "destructive";
      default: return "outline";
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Return Processing SLA</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="RMA ID">
            <Input value={rmaId} onChange={(e) => setRmaId(e.target.value)} placeholder="uuid" />
          </Field>
          <div className="flex items-end">
            <Button onClick={run} disabled={busy} variant="secondary">Compute</Button>
          </div>
        </div>
        {result && (
          <div className="rounded border border-border bg-muted/30 p-3 text-sm space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{result.rmaNumber}</span>
              {result.rule ? (
                <>
                  <Badge variant="secondary">{result.rule.name}</Badge>
                  <Badge variant="outline">{result.rule.enforcement}</Badge>
                </>
              ) : (
                <Badge variant="outline">no rule matches</Badge>
              )}
              <span className="text-xs text-muted-foreground">Received: {fmt(result.receivedAt)}</span>
            </div>
            {!result.receivedAt && (
              <div className="text-xs text-muted-foreground">SLA clock starts at physical receipt. This RMA has not been received yet.</div>
            )}
            {result.stages?.length > 0 && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Stage</TableHead>
                    <TableHead>Within</TableHead>
                    <TableHead>Arrival Date</TableHead>
                    <TableHead>Completed</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Remaining</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {result.stages.map((s: any) => (
                    <TableRow key={s.key}>
                      <TableCell className="text-xs">{s.label}</TableCell>
                      <TableCell className="text-xs tabular-nums">{s.withinHours}h</TableCell>
                      <TableCell className="text-xs">{fmt(s.dueAt)}</TableCell>
                      <TableCell className="text-xs">{fmt(s.completedAt)}</TableCell>
                      <TableCell><Badge variant={statusVariant(s.status)}>{s.status}</Badge></TableCell>
                      <TableCell className="text-xs text-right tabular-nums">{fmtRem(s.minutesRemaining)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <div className="text-xs text-muted-foreground">
              Warehouse-owned processing clock: receipt → inspection → disposition → recovery.
              Refund and payment timing remain owned by external systems.
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}